import { createVNode, render } from 'vue';
import LoadingComponent from './index.vue';

/**
 * Loading 实例
 */
interface LoadingInstance {
  show: () => void;
  hide: () => void;
  destroy: () => void;
}

/**
 * 全局 Loading 实例
 */
let globalLoadingInstance: LoadingInstance | null = null;
let requestCount = 0;

/**
 * 创建 Loading 实例
 * @returns Loading 实例
 */
function createLoading(): LoadingInstance {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const vnode = createVNode(LoadingComponent, {
    onDestroy: () => {
      render(null, container);
      document.body.removeChild(container);
    }
  });

  render(vnode, container);

  const instance = vnode.component?.exposed as LoadingInstance;

  return {
    show: () => instance?.show(),
    hide: () => instance?.hide(),
    destroy: () => {
      instance?.hide();
      setTimeout(() => {
        render(null, container);
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      }, 300);
    }
  };
}

/**
 * 获取或创建全局 Loading 实例
 * @returns Loading 实例
 */
function getOrCreateLoading(): LoadingInstance {
  if (!globalLoadingInstance) {
    globalLoadingInstance = createLoading();
  }
  return globalLoadingInstance;
}

/**
 * Loading 服务主函数
 * @returns Loading 实例
 */
function Loading(): LoadingInstance {
  const instance = getOrCreateLoading();
  
  requestCount++;
  instance.show();
  
  return instance;
}

/**
 * 显示 Loading
 * @returns Loading 实例
 */
Loading.show = (): LoadingInstance => {
  return Loading();
};

/**
 * 隐藏 Loading
 */
Loading.hide = (): void => {
  requestCount--;
  if (requestCount <= 0) {
    requestCount = 0;
    if (globalLoadingInstance) {
      globalLoadingInstance.hide();
    }
  }
};

/**
 * 强制隐藏 Loading（重置计数器）
 */
Loading.forceHide = (): void => {
  requestCount = 0;
  if (globalLoadingInstance) {
    globalLoadingInstance.hide();
  }
};

/**
 * 获取当前请求计数
 */
Loading.getCount = (): number => {
  return requestCount;
};

/**
 * 销毁 Loading 实例
 */
Loading.destroy = (): void => {
  if (globalLoadingInstance) {
    globalLoadingInstance.destroy();
    globalLoadingInstance = null;
  }
  requestCount = 0;
};

export default Loading;
export type { LoadingInstance }; 